/* The estate map: every profiled table placed by its baseline and how long ago it was profiled.
 *
 * **A table with no baseline is counted, never placed.** The map's vertical axis is the baseline, and a
 * table nobody profiled, or one whose profile measured nothing, has none — drawing it at the bottom would
 * read as the worst table in the estate. The note under the map says how many were left off. */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { SCREEN_PATHS } from '@/shared/routes';

import {
  AGEING_WITHIN_HOURS,
  FRESH_WITHIN_HOURS,
  MAP_AGE_TICKS,
  ageAxis,
  baselineAxis,
  baselineTone,
  bubbleSize,
  hoursSince,
} from '../model/estate';
import type { Tone } from '../model/estate';

export interface MapTable {
  readonly id: string;
  readonly name: string;
  /** The label beside the bubble: the table without its schema, which the tooltip carries. */
  readonly objectName: string;
  readonly baseline: number | null;
  readonly profiledAt: string | null;
  readonly rows: number | null;
}

/** The three freshness zones behind the map, as stretches of its age axis. */
const ZONES: readonly {
  readonly key: 'fresh' | 'ageing' | 'stale';
  readonly tone: Tone;
  readonly from: number;
  readonly to: number;
}[] = [
  { key: 'fresh', tone: 'good', from: 0, to: ageAxis(FRESH_WITHIN_HOURS) },
  {
    key: 'ageing',
    tone: 'watch',
    from: ageAxis(FRESH_WITHIN_HOURS),
    to: ageAxis(AGEING_WITHIN_HOURS),
  },
  { key: 'stale', tone: 'risk', from: ageAxis(AGEING_WITHIN_HOURS), to: 100 },
];

/** The baseline rules across the map; the two band edges are dashed. */
const BASELINE_RULES: readonly number[] = [50, 75, 90, 100];

export function EstateMap({
  tables,
  total,
  now,
}: {
  readonly tables: readonly MapTable[];
  /** Every table in the estate, placed or not, so the note can say how many were left off. */
  readonly total: number;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const placed = tables.flatMap((table) => {
    if (table.baseline === null || table.profiledAt === null) return [];
    const hours = hoursSince(table.profiledAt, now);
    return hours === null
      ? []
      : [{ ...table, baseline: table.baseline, profiledAt: table.profiledAt, hours }];
  });
  const largest = Math.max(1, ...placed.map((table) => table.rows ?? 0));

  return (
    <article className="lq-pf__panel lq-pf__panel--map">
      <div className="lq-pf__panelHead lq-pf__panelHead--wrap">
        <h2 className="lq-pf__panelTitle lq-pf__grow">{t('profiles.estateMap.title')}</h2>
        <span className="lq-pf__panelSub">{t('profiles.estateMap.sub')}</span>
      </div>
      <div
        className="lq-pf__plot lq-pf__plot--map"
        role="group"
        aria-label={t('profiles.estateMap.label')}
      >
        {ZONES.map((zone) => (
          <div
            key={zone.key}
            className="lq-pf__zone"
            data-tone={zone.tone}
            style={{
              left: `${zone.from.toFixed(2)}%`,
              width: `${(zone.to - zone.from).toFixed(2)}%`,
            }}
          >
            <span className="lq-pf__zoneLabel">
              {t(`profiles.freshness.${zone.key}`)}
            </span>
          </div>
        ))}
        {BASELINE_RULES.map((value) => (
          <div
            key={value}
            className="lq-pf__rule"
            data-dashed={value === 75 || value === 90}
            style={{ bottom: `${baselineAxis(value).toFixed(1)}%` }}
            aria-hidden="true"
          >
            <span className="lq-pf__ruleLabel">{value}</span>
          </div>
        ))}
        {MAP_AGE_TICKS.map((tick) => (
          <span
            key={tick.hours}
            className="lq-pf__axisTick"
            style={{ left: `${ageAxis(tick.hours).toFixed(2)}%` }}
            aria-hidden="true"
          >
            {t(`profiles.estateMap.tick.${tick.unit}`, { amount: tick.amount })}
          </span>
        ))}
        {placed.map((table) => {
          const size = bubbleSize(table.rows ?? 0, largest);
          const age = ageText(t, table.profiledAt, now) ?? table.profiledAt;
          return (
            <Link
              key={table.id}
              className="lq-pf__bubble"
              data-tone={baselineTone(table.baseline)}
              to={generatePath(SCREEN_PATHS.profile, { datasetId: table.id })}
              aria-label={
                table.rows === 0
                  ? t('profiles.estateMap.bubbleEmpty', {
                      name: table.name,
                      baseline: table.baseline,
                      age,
                    })
                  : t('profiles.estateMap.bubble', {
                      name: table.name,
                      baseline: table.baseline,
                      age,
                      rows: compactNumber(table.rows ?? 0),
                    })
              }
              style={{
                left: `${ageAxis(table.hours).toFixed(2)}%`,
                bottom: `${baselineAxis(table.baseline).toFixed(2)}%`,
              }}
            >
              <span
                className="lq-pf__bubbleMark"
                style={{ width: `${String(size)}px`, height: `${String(size)}px` }}
              />
              <span className="lq-pf__bubbleLabel" aria-hidden="true">
                {table.objectName}
              </span>
            </Link>
          );
        })}
      </div>
      <p className="lq-pf__plotNote">
        {t('profiles.estateMap.note', { count: total - placed.length })}
      </p>
    </article>
  );
}
